import {
  PDI_ACCESS,
  PDI_APPLICABILITY,
  PDI_RESULTS,
  type PdiAccess,
  type PdiApplicability,
  type PdiResult,
  pdiMeasurementVerdict,
} from '@felix/domain';
import {
  Alert,
  Button,
  Drawer,
  Group,
  Modal,
  NumberInput,
  Progress,
  SegmentedControl,
  Select,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendarDue,
  IconCheck,
  IconChecklist,
  IconCircleOff,
  IconClipboardCheck,
  IconEdit,
  IconEye,
  IconListCheck,
  IconLock,
  IconPlus,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { DateTimeLtUtcInput } from '../../inputs/DateTimeLtUtcInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import { OwnerAttachments } from '../pms/OwnerAttachments.js';
import { usePmsQuery } from '../pms/PmsWorkParts.js';
import lib from '../pms-library/PmsLibrary.module.css';
import { LoadError, TablePager, TableSkeleton } from '../purchasing/table.js';
import classes from './Drydock.module.css';
import { useDrydockErrorText } from './errors.js';
import {
  type CheckpointRow,
  checkpointRows,
  INSPECTION_FILTERS,
  type InspectionFilter,
  inspectionOptions,
  latestByCode,
  pickInspection,
  revisionsOf,
  STATUS_TONE,
  statusOf,
  vesselNotApplicable,
} from './inspection-view.js';
import { DrydockReportButtons } from './ReportButtons.js';
import { useDrydockReportExport } from './report-export.js';
import {
  CheckpointScope,
  FindingTable,
  JobTable,
  type ScopeOpen,
  ScopeOverlays,
  useDrydockScope,
} from './ScopePanels.js';
import type { DrydockScopeSource, PdiInspectionSource } from './source.js';
import type {
  DrydockProjects,
  PdiApplicabilityRow,
  PdiInspectionWork,
  PdiMeasurementRow,
  PdiProgressCounts,
  PdiResultInput,
  PdiResultRow,
  PdiTreeArea,
} from './types.js';
import { PAGE_SIZE, pageOf, pagesOf } from './view.js';

export interface PdiInspectionScreenProps {
  source: PdiInspectionSource;
  /** Bulgu ve işler (Faz 3; ADR 0129 Ek Faz 3): verilmezse sekmeler ve bölüm görünmez. */
  scope?: DrydockScopeSource;
}

type Loaded<T> = { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; data: T };

const ALL = 'all';

/**
 * Maintenance (PMS) ▸ Dry Docking ▸ Pre-Docking Inspection (ADR 0129 Ek Faz 2; Excel "Software
 * Workflow" 2–5): denetimin alan → sistem → kontrol noktası ağacı, ilerleme, nokta ayrıntısı ve
 * sonuç. Yalnız kontrol noktası denetlenir. Gemide sonuç kaydedilir, gerekçeyle düzeltilir (yeni
 * revizyon), Master ya da Chief Engineer inceler, gemi profili (uygunluk) belirlenir ve kanıt
 * eklenir; ofiste salt okunur (gemi verisi, mimari belge §8). Bulgu ve iş (Faz 3) sekmelerde ve nokta
 * çekmecesinde elle açılır; sonuç hiçbirini açmaz (R4).
 */
export function PdiInspectionScreen({ source, scope }: PdiInspectionScreenProps) {
  const { t } = useTranslation();
  const [query, setQuery] = usePmsQuery();
  const office = source.scope === 'office';
  const vesselId = office ? query.get('vessel') : null;
  const wanted = query.get('inspection');
  const [projects, setProjects] = useState<Loaded<DrydockProjects>>({ kind: 'loading' });
  const [work, setWork] = useState<Loaded<PdiInspectionWork> | null>(null);

  const loadProjects = useCallback(async () => {
    try {
      setProjects({ kind: 'ready', data: await source.projects(vesselId) });
    } catch (error) {
      setProjects({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const options = useMemo(
    () => (projects.kind === 'ready' ? inspectionOptions(projects.data.projects) : []),
    [projects],
  );
  const inspectionId = pickInspection(options, wanted);

  const loadWork = useCallback(async () => {
    if (!inspectionId) {
      setWork(null);
      return;
    }
    try {
      setWork({ kind: 'ready', data: await source.work(inspectionId) });
    } catch (error) {
      setWork({ kind: 'error', code: errorCode(error) });
    }
  }, [source, inspectionId]);
  useEffect(() => {
    setWork({ kind: 'loading' });
    void loadWork();
  }, [loadWork]);

  const header = (
    <PageHeader
      title={t('drydock.work.title')}
      description={t(office ? 'drydock.work.officeDescription' : 'drydock.work.description')}
    />
  );

  if (projects.kind !== 'ready') {
    return (
      <div className={lib.screen}>
        {header}
        {projects.kind === 'loading' ? (
          <TableSkeleton columns={6} label={t('drydock.work.title')} />
        ) : (
          <Panel>
            <LoadError
              code={projects.code}
              title={t('drydock.work.error')}
              onRetry={() => {
                setProjects({ kind: 'loading' });
                void loadProjects();
              }}
            />
          </Panel>
        )}
      </div>
    );
  }

  const vessels = projects.data.vessels ?? [];
  return (
    <div className={lib.screen}>
      {header}
      <div className={classes.filters}>
        {office ? (
          <Select
            label={t('drydock.work.vessel')}
            data={[
              { value: ALL, label: t('drydock.work.allVessels') },
              ...vessels.map((v) => ({ value: v.id, label: v.name })),
            ]}
            value={vesselId ?? ALL}
            allowDeselect={false}
            onChange={(value) =>
              setQuery({ vessel: value === ALL ? null : value, inspection: null })
            }
          />
        ) : null}
        <Select
          label={t('drydock.work.inspection')}
          placeholder={t('drydock.work.noInspection')}
          data={options.map(({ inspection, project }) => ({
            value: inspection.id,
            label: `${inspection.inspectionNo} · ${office && project.vesselName ? `${project.vesselName} · ` : ''}${project.title} (${t(`drydock.inspectionStates.${inspection.state}`)})`,
          }))}
          value={inspectionId}
          allowDeselect={false}
          searchable
          onChange={(value) => setQuery({ inspection: value })}
        />
      </div>
      {/* Excel "Notes": taslak rehber, klas onaylı değil (ADR 0129 R18) */}
      <p className={classes.muted}>{t('drydock.library.disclaimer')}</p>
      {options.length === 0 ? (
        <Panel>
          {projects.data.libraryPublished === false ? (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} aria-hidden />}>
              {t('drydock.work.noLibrary')}
            </Alert>
          ) : null}
          <EmptyState title={t('drydock.work.empty')} />
        </Panel>
      ) : work === null || work.kind === 'loading' ? (
        <TableSkeleton columns={6} label={t('drydock.work.title')} />
      ) : work.kind === 'error' ? (
        <Panel>
          <LoadError
            code={work.code}
            title={t('drydock.work.error')}
            onRetry={() => {
              setWork({ kind: 'loading' });
              void loadWork();
            }}
          />
        </Panel>
      ) : (
        <InspectionWork
          key={work.data.id}
          work={work.data}
          source={source}
          {...(scope ? { scope } : {})}
          onChanged={(next) => setWork({ kind: 'ready', data: next })}
          onReload={loadWork}
        />
      )}
    </div>
  );
}

/** Denetimin özeti, ilerlemesi, ağacı ve nokta tablosu. */
export function InspectionWork({
  work,
  source,
  scope: scopeSource,
  onChanged,
  onReload,
}: {
  work: PdiInspectionWork;
  source: PdiInspectionSource;
  scope?: DrydockScopeSource;
  onChanged: (work: PdiInspectionWork) => void;
  onReload: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const scoped = useDrydockScope(scopeSource, work.projectId);
  const scope = scoped.scope?.kind === 'ready' ? scoped.scope.data : null;
  const [tab, setTab] = useState<'checklist' | 'findings' | 'jobs'>('checklist');
  const [scopeOpen, setScopeOpen] = useState<ScopeOpen | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState<InspectionFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openCode, setOpenCode] = useState<string | null>(null);
  const latest = useMemo(() => latestByCode(work.results), [work.results]);
  const na = useMemo(() => vesselNotApplicable(work.applicability), [work.applicability]);
  const queue = useMemo(() => new Set(work.afterDockingQueue ?? []), [work.afterDockingQueue]);
  const rows = useMemo(
    () => checkpointRows(work.tree, { latest, na, selected, filter, query: search, queue }),
    [work.tree, latest, na, selected, filter, search, queue],
  );
  const [closing, setClosing] = useState(false);
  const reports = useDrydockReportExport(source);
  const pages = pagesOf(rows.length);
  const current = Math.min(page, pages);
  const shown = pageOf(rows, current);
  const total = work.progress.total;
  const percent = total.applicable === 0 ? 0 : Math.round((total.done / total.applicable) * 100);
  const choose = (code: string | null) => {
    setSelected(code);
    setPage(1);
  };
  const k = (key: string) => t(`drydock.work.columns.${key}`);
  const open = work.state === 'open';

  return (
    <>
      <Panel>
        <div className={classes.workHead}>
          <div>
            <strong className={classes.mono}>{work.inspectionNo}</strong>
            <span className={classes.muted}>
              {' '}
              · {work.projectTitle}
              {work.vesselName ? ` · ${work.vesselName}` : ''} ·{' '}
              {t('drydock.projects.libraryVersion', {
                version: work.libraryVersion ?? '—',
                count: work.checkpoints ?? 0,
              })}{' '}
              · {formatDay(work.openedOn)}
            </span>
          </div>
          <Group gap="xs">
            {work.kind ? (
              <StatusBadge tone="neutral">{t(`drydock.yard.kinds.${work.kind}`)}</StatusBadge>
            ) : null}
            <StatusBadge tone={open ? 'info' : 'neutral'}>
              {t(`drydock.inspectionStates.${work.state}`)}
            </StatusBadge>
            {/* Faz 5: denetim raporu (açık denetim "INSPECTION OPEN – PROVISIONAL") */}
            <DrydockReportButtons
              size="compact-sm"
              label={t('drydockReport.actions.pdi')}
              busy={reports.busy}
              run={(mode) =>
                reports.inspection(
                  work,
                  { source, ...(scopeSource ? { scope: scopeSource } : {}) },
                  mode,
                )
              }
            />
            {open && work.side === 'ship' && work.can.review && source.closeInspection ? (
              <Button
                size="compact-sm"
                leftSection={<IconLock size={14} aria-hidden />}
                onClick={() => setClosing(true)}
              >
                {t('drydock.yard.actions.closeInspection')}
              </Button>
            ) : null}
          </Group>
        </div>
        <Progress
          value={percent}
          aria-label={t('drydock.work.progress', { percent })}
          className={classes.progress}
        />
        <p className={classes.muted}>
          {t('drydock.work.progressText', {
            done: total.done,
            applicable: total.applicable,
            percent,
          })}
        </p>
      </Panel>
      {open ? null : <Alert color="gray">{t('drydock.work.closed')}</Alert>}
      {work.kind === 'after_docking' && queue.size > 0 ? (
        <Alert color="blue">
          {t('drydock.yard.queue')}: {t('drydock.yard.queueHint', { count: queue.size })}
        </Alert>
      ) : null}
      {closing && source.closeInspection ? (
        <CloseInspectionModal
          work={work}
          source={source}
          onClose={() => setClosing(false)}
          onDone={async () => {
            setClosing(false);
            await onReload();
          }}
        />
      ) : null}
      <ProgressTiles counts={total} />
      {scope ? (
        <SegmentedControl
          aria-label={t('drydock.scope.tabs.label')}
          data={[
            { value: 'checklist', label: t('drydock.scope.tabs.checklist') },
            {
              value: 'findings',
              label: t('drydock.scope.tabs.findings', { count: scope.counts.findings }),
            },
            { value: 'jobs', label: t('drydock.scope.tabs.jobs', { count: scope.counts.jobs }) },
          ]}
          value={tab}
          onChange={(value) => setTab(value as typeof tab)}
        />
      ) : null}
      {scope && tab === 'findings' ? (
        <Panel title={t('drydock.scope.findings')} flush>
          <FindingTable
            rows={scope.findings}
            onOpen={(id) => setScopeOpen({ kind: 'finding', id })}
          />
        </Panel>
      ) : null}
      {scope && tab === 'jobs' ? (
        <Panel title={t('drydock.scope.jobs')} flush>
          {/* Ek iş (Faz 4, ADR 0129 #1949): tersanede kaynaksız açılır */}
          {scope.side === 'ship' &&
          scope.project.state === 'in_yard' &&
          scope.can.create &&
          scopeSource?.saveJob ? (
            <div className={lib.toolbar}>
              <Button
                variant="default"
                leftSection={<IconPlus size={16} aria-hidden />}
                onClick={() => setScopeOpen({ kind: 'newJob' })}
              >
                {t('drydock.yard.actions.additionalJob')}
              </Button>
            </div>
          ) : null}
          <JobTable rows={scope.jobs} onOpen={(j) => setScopeOpen({ kind: 'job', id: j.id })} />
        </Panel>
      ) : null}
      <div
        className={lib.split}
        style={scope !== null && tab !== 'checklist' ? { display: 'none' } : undefined}
      >
        <InspectionTree
          tree={work.tree}
          progress={work.progress}
          selected={selected}
          onSelect={choose}
        />
        <Panel title={t('drydock.library.checkpoints')} flush>
          <div className={lib.toolbar}>
            <TextInput
              className={lib.search}
              aria-label={t('drydock.search')}
              placeholder={t('drydock.search')}
              leftSection={<IconSearch size={16} aria-hidden />}
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
                setPage(1);
              }}
            />
            <SegmentedControl
              aria-label={t('drydock.work.filter')}
              data={INSPECTION_FILTERS.map((value) => ({
                value,
                label: t(`drydock.work.filters.${value}`),
              }))}
              value={filter}
              onChange={(value) => {
                setFilter(value as InspectionFilter);
                setPage(1);
              }}
            />
          </div>
          {rows.length === 0 ? (
            <EmptyState title={t('drydock.empty.filtered')} />
          ) : (
            <>
              <CheckpointTable rows={shown} onOpen={setOpenCode} />
              <TablePager
                page={current}
                pages={pages}
                summary={t('drydock.showing', {
                  from: (current - 1) * PAGE_SIZE + 1,
                  to: Math.min(current * PAGE_SIZE, rows.length),
                  total: rows.length,
                })}
                label={k('pages')}
                onPage={setPage}
              />
            </>
          )}
        </Panel>
      </div>
      <CheckpointWorkDrawer
        code={openCode}
        work={work}
        source={source}
        onClose={() => setOpenCode(null)}
        onChanged={onChanged}
        onReload={onReload}
        scopePanel={
          scope && openCode ? (
            <CheckpointScope
              scope={scope}
              code={openCode}
              inspectionId={work.id}
              canAdd={work.state === 'open' && !na.has(openCode)}
              onOpen={setScopeOpen}
            />
          ) : null
        }
      />
      {scope && scopeSource ? (
        <ScopeOverlays
          scope={scope}
          source={scopeSource}
          open={scopeOpen}
          onOpen={setScopeOpen}
          onClose={() => setScopeOpen(null)}
          onChanged={scoped.set}
        />
      ) : null}
    </>
  );
}

/** İlerleme kartları: uygulanabilir, tamamlanan, kalan, olumsuz, After Docking, N/A. */
export function ProgressTiles({ counts }: { counts: PdiProgressCounts }) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drydock.work.stats.${key}`);
  return (
    <div className={classes.stats}>
      <StatTile
        icon={<IconListCheck size={20} />}
        label={k('applicable')}
        value={counts.applicable}
        wrapLabel
      />
      <StatTile
        icon={<IconCheck size={20} />}
        label={k('done')}
        value={counts.done}
        tone="success"
        wrapLabel
      />
      <StatTile
        icon={<IconChecklist size={20} />}
        label={k('remaining')}
        value={counts.remaining}
        wrapLabel
      />
      <StatTile
        icon={<IconAlertTriangle size={20} />}
        label={k('adverse')}
        value={counts.adverse}
        tone={counts.adverse > 0 ? 'danger' : 'default'}
        wrapLabel
      />
      <StatTile
        icon={<IconCalendarDue size={20} />}
        label={k('deferred')}
        value={counts.deferred}
        tone={counts.deferred > 0 ? 'warning' : 'default'}
        wrapLabel
      />
      <StatTile
        icon={<IconCircleOff size={20} />}
        label={k('notApplicable')}
        value={counts.notApplicable}
        wrapLabel
      />
    </div>
  );
}

/** Sol ağaç: alan → sistem, tamamlanan / uygulanabilir sayısıyla. */
export function InspectionTree({
  tree,
  progress,
  selected,
  onSelect,
}: {
  tree: readonly PdiTreeArea[];
  progress: PdiInspectionWork['progress'];
  selected: string | null;
  onSelect: (code: string | null) => void;
}) {
  const { t } = useTranslation();
  const ratio = (c: PdiProgressCounts | undefined) => (c ? `${c.done}/${c.applicable}` : '0/0');
  return (
    <Panel title={t('drydock.library.tree')} flush>
      <nav className={lib.systemList} aria-label={t('drydock.library.tree')}>
        <button
          type="button"
          className={`${lib.systemButton} ${classes.treeButton}`}
          data-active={selected === null || undefined}
          aria-pressed={selected === null}
          onClick={() => onSelect(null)}
        >
          <span className={lib.systemCode}>—</span>
          <span className={lib.systemName}>{t('drydock.library.allAreas')}</span>
          <span className={lib.count}>{ratio(progress.total)}</span>
        </button>
        {tree.map((area) => (
          <div key={area.code}>
            <button
              type="button"
              className={`${lib.systemButton} ${classes.treeButton}`}
              data-active={selected === area.code || undefined}
              aria-pressed={selected === area.code}
              onClick={() => onSelect(area.code)}
              title={area.name}
            >
              <span className={`${lib.systemCode} ${classes.code}`}>{area.code.slice(4)}</span>
              <span className={lib.systemName}>{area.name}</span>
              <span className={lib.count}>{ratio(progress.areas[area.code])}</span>
            </button>
            {area.systems.map((system) => (
              <button
                key={system.code}
                type="button"
                className={`${lib.systemButton} ${classes.treeButton} ${classes.subButton}`}
                data-active={selected === system.code || undefined}
                aria-pressed={selected === system.code}
                onClick={() => onSelect(system.code)}
                title={system.name}
              >
                <span className={`${lib.systemCode} ${classes.code}`}>{system.code.slice(4)}</span>
                <span className={lib.systemName}>{system.name}</span>
                <span className={lib.count}>{ratio(progress.systems[system.code])}</span>
              </button>
            ))}
          </div>
        ))}
      </nav>
    </Panel>
  );
}

/** Nokta tablosu: ID, Checkpoint, System, Status, Inspected (LT), Inspector, Evidence. */
export function CheckpointTable({
  rows,
  onOpen,
}: {
  rows: readonly CheckpointRow[];
  onOpen: (code: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drydock.work.columns.${key}`);
  return (
    <section
      className={table.tableWrap}
      aria-label={t('drydock.library.checkpointList')}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
      tabIndex={0}
    >
      <table className={`${table.table} ${classes.checkpointTable}`}>
        <thead>
          <tr>
            <th scope="col">{k('code')}</th>
            <th scope="col">{k('checkpoint')}</th>
            <th scope="col">{k('system')}</th>
            <th scope="col">{k('status')}</th>
            <th scope="col">{k('inspected')}</th>
            <th scope="col">{k('inspector')}</th>
            <th scope="col">{k('evidence')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.checkpoint.code}>
              <td className={classes.mono}>{row.checkpoint.code}</td>
              <td>
                <button
                  type="button"
                  className={classes.linkButton}
                  onClick={() => onOpen(row.checkpoint.code)}
                >
                  {row.checkpoint.name}
                </button>
              </td>
              <td>{row.systemName}</td>
              <td>
                <StatusBadge tone={STATUS_TONE[row.status]}>
                  {t(`drydock.work.status.${row.status}`)}
                </StatusBadge>
              </td>
              <td className={classes.mono}>
                {row.latest ? formatDateTime(row.latest.inspectedAt, row.latest.tzOffsetMin) : '—'}
              </td>
              <td>{row.latest?.inspectorName ?? '—'}</td>
              <td className={classes.mono}>{row.latest ? row.latest.attachments : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Ölçümlerin tablosu; sınır yoksa karar "No limit" (pass/fail verilmez). */
export function MeasurementTable({ rows }: { rows: readonly PdiMeasurementRow[] }) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drydock.resultFields.${key}`);
  if (rows.length === 0) return null;
  return (
    <section
      className={table.tableWrap}
      aria-label={k('measurements')}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
      tabIndex={0}
    >
      <table className={`${table.table} ${classes.checkpointTable}`}>
        <thead>
          <tr>
            <th scope="col">{k('label')}</th>
            <th scope="col">{k('value')}</th>
            <th scope="col">{k('limits')}</th>
            <th scope="col">{k('verdict')}</th>
            <th scope="col">{k('reference')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m, index) => {
            const verdict = pdiMeasurementVerdict(m);
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: ölçüm satırının kimliği yok, sıra sabittir
              <tr key={index}>
                <td>{m.label}</td>
                <td className={classes.mono}>
                  {m.value}
                  {m.unit ? ` ${m.unit}` : ''}
                </td>
                <td className={classes.mono}>
                  {m.lower === null && m.upper === null
                    ? '—'
                    : `${m.lower ?? '…'} – ${m.upper ?? '…'}`}
                </td>
                <td>
                  <StatusBadge
                    tone={
                      verdict === 'pass' ? 'success' : verdict === 'fail' ? 'danger' : 'neutral'
                    }
                  >
                    {t(`drydock.work.verdict.${verdict ?? 'none'}`)}
                  </StatusBadge>
                </td>
                <td>{m.reference ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function CheckpointWorkDrawer({
  code,
  work,
  source,
  onClose,
  onChanged,
  onReload,
  scopePanel,
}: {
  code: string | null;
  work: PdiInspectionWork;
  source: PdiInspectionSource;
  onClose: () => void;
  onChanged: (work: PdiInspectionWork) => void;
  onReload: () => Promise<void>;
  /** Bu noktanın bulgu ve işleri (Faz 3). */
  scopePanel?: ReactNode;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<'result' | 'applicability' | 'review' | null>(null);
  // Başka nokta açılınca ileti ve form sıfırlanır (önceki değer kalıbı)
  const [seen, setSeen] = useState(code);
  if (seen !== code) {
    setSeen(code);
    setError(null);
    setForm(null);
  }
  const found = useMemo(() => {
    for (const area of work.tree) {
      for (const system of area.systems) {
        const checkpoint = system.checkpoints.find((c) => c.code === code);
        if (checkpoint) return { area, system, checkpoint };
      }
    }
    return null;
  }, [work.tree, code]);
  const latest = useMemo(() => latestByCode(work.results), [work.results]);
  const na = useMemo(() => vesselNotApplicable(work.applicability), [work.applicability]);
  const revisions = code ? revisionsOf(work.results, code) : [];
  const current = revisions[0] ?? null;
  const status = code ? statusOf(code, latest, na) : 'not_inspected';
  const profile = work.applicability.find((a) => a.checkpointCode === code) ?? null;
  const ship = work.side === 'ship';
  const open = work.state === 'open';
  const canRecord =
    ship && open && work.can.record && Boolean(source.saveResult) && !na.has(code ?? '');
  const canApplicability = ship && work.can.applicability && Boolean(source.setApplicability);
  // Kendi kaydını (denetçi ya da yazan) yalnız şirket ayarı açıkken inceler (ADR 0129 Ek; sahip
  // kararı 2026-10-26)
  const ownRecord =
    !work.selfReviewAllowed &&
    current !== null &&
    work.viewerId !== null &&
    (current.inspectorId === work.viewerId || current.createdBy === work.viewerId);
  const canReview =
    ship &&
    open &&
    work.can.review &&
    Boolean(source.review) &&
    current !== null &&
    current.review === null &&
    !ownRecord;
  const f = (key: string) => t(`drydock.resultFields.${key}`);

  return (
    <>
      <Drawer
        opened={found !== null}
        onClose={onClose}
        closeButtonProps={{ 'aria-label': t('drydock.close') }}
        position="right"
        size="xl"
        title={found ? `${found.checkpoint.code} · ${found.checkpoint.name}` : ''}
      >
        {found ? (
          <div className={classes.detail}>
            <p className={classes.path}>
              {t('drydock.detail.path', { area: found.area.name, system: found.system.name })}
            </p>
            <Group gap="xs">
              <StatusBadge tone={STATUS_TONE[status]}>
                {t(`drydock.work.status.${status}`)}
              </StatusBadge>
              <StatusBadge tone="neutral">
                {t(`drydock.work.applicability.${profile?.applicability ?? 'review'}`)}
              </StatusBadge>
            </Group>
            {error ? <Alert color="red">{error}</Alert> : null}
            <dl className={classes.fields}>
              {(['scope', 'method', 'timing', 'acceptance'] as const).map((key) => (
                <div key={key}>
                  <dt>{t(`drydock.fields.${key}`)}</dt>
                  <dd>{found.checkpoint.content[key]}</dd>
                </div>
              ))}
              {profile?.reason ? (
                <div>
                  <dt>{f('applicabilityReason')}</dt>
                  <dd>{profile.reason}</dd>
                </div>
              ) : null}
            </dl>
            <Group justify="flex-end" gap="xs">
              {canApplicability ? (
                <Button
                  variant="default"
                  leftSection={<IconCircleOff size={16} aria-hidden />}
                  onClick={() => setForm('applicability')}
                >
                  {t('drydock.work.actions.applicability')}
                </Button>
              ) : null}
              {canReview ? (
                <Button
                  variant="default"
                  leftSection={<IconEye size={16} aria-hidden />}
                  onClick={() => setForm('review')}
                >
                  {t('drydock.work.actions.review')}
                </Button>
              ) : null}
              {canRecord ? (
                <Button
                  leftSection={
                    current ? (
                      <IconEdit size={16} aria-hidden />
                    ) : (
                      <IconClipboardCheck size={16} aria-hidden />
                    )
                  }
                  onClick={() => setForm('result')}
                >
                  {t(current ? 'drydock.work.actions.correct' : 'drydock.work.actions.record')}
                </Button>
              ) : null}
            </Group>
            <Panel title={t('drydock.work.result')}>
              {current ? (
                <ResultDetail result={current} />
              ) : (
                <p className={classes.muted}>
                  {t(
                    na.has(found.checkpoint.code)
                      ? 'drydock.work.vesselNa'
                      : 'drydock.work.noResult',
                  )}
                </p>
              )}
            </Panel>
            {current && source.attachments ? (
              <Panel title={f('evidence')}>
                <OwnerAttachments
                  key={current.id}
                  ownerId={current.id}
                  list={source.attachments}
                  upload={ship && open && work.can.record ? source.upload : undefined}
                  href={source.attachmentUrl}
                  emptyText={f('noEvidence')}
                  addLabel={f('addEvidence')}
                  label={f('evidence')}
                  onError={(err) => setError(errorText(err))}
                />
              </Panel>
            ) : null}
            {revisions.length > 1 ? (
              <Panel title={t('drydock.work.history')}>
                <ul className={classes.inspections}>
                  {revisions.map((r) => (
                    <li key={r.id}>
                      <span className={classes.mono}>
                        {t('drydock.work.revision', { no: r.revision })}
                      </span>
                      <StatusBadge tone={STATUS_TONE[r.result]}>
                        {t(`drydock.work.status.${r.result}`)}
                      </StatusBadge>
                      <span className={classes.muted}>
                        {formatDateTime(r.createdAt, r.tzOffsetMin)} · {r.createdByName ?? '—'}
                        {r.correctionReason ? ` · ${r.correctionReason}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>
            ) : null}
            {/* Bulgu ve iş elle açılır: sonuç kaydı hiçbirini açmaz (R4; ADR 0129 Ek Faz 3) */}
            {scopePanel}
          </div>
        ) : null}
      </Drawer>
      {found && form === 'result' ? (
        <ResultFormModal
          code={found.checkpoint.code}
          work={work}
          current={current}
          source={source}
          onClose={() => setForm(null)}
          onSaved={(next) => {
            setForm(null);
            onChanged(next);
          }}
        />
      ) : null}
      {found && form === 'applicability' ? (
        <ApplicabilityModal
          code={found.checkpoint.code}
          profile={profile}
          source={source}
          onClose={() => setForm(null)}
          onSaved={async () => {
            setForm(null);
            await onReload();
          }}
        />
      ) : null}
      {current && form === 'review' ? (
        <ReviewModal
          result={current}
          source={source}
          onClose={() => setForm(null)}
          onSaved={(next) => {
            setForm(null);
            onChanged(next);
          }}
        />
      ) : null}
    </>
  );
}

/** Son revizyonun ayrıntısı: sonuç, erişim, zaman, denetçi, ekipman, not, ölçümler, inceleme. */
export function ResultDetail({ result }: { result: PdiResultRow }) {
  const { t } = useTranslation();
  const f = (key: string) => t(`drydock.resultFields.${key}`);
  const fields: [string, string][] = [
    ['result', t(`drydock.work.status.${result.result}`)],
    ['access', t(`drydock.work.access.${result.access}`)],
    ['inspectedAt', formatDateTime(result.inspectedAt, result.tzOffsetMin)],
    ['inspector', result.inspectorName ?? '—'],
    ['equipment', result.equipmentName ?? '—'],
    ['notes', result.notes ?? '—'],
  ];
  if (result.correctionReason) fields.push(['correctionReason', result.correctionReason]);
  return (
    <div className={classes.detail}>
      <dl className={classes.fields}>
        {fields.map(([key, value]) => (
          <div key={key}>
            <dt>{f(key)}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <MeasurementTable rows={result.measurements} />
      <p className={classes.muted}>
        {t('drydock.work.revisionBy', {
          no: result.revision,
          name: result.createdByName ?? '—',
          date: formatDateTime(result.createdAt, result.tzOffsetMin),
        })}
      </p>
      {result.review ? (
        <Alert color="green" icon={<IconEye size={16} aria-hidden />}>
          {t('drydock.work.reviewed', {
            name: result.review.reviewerName ?? '—',
            date: formatDateTime(result.review.createdAt, result.tzOffsetMin),
          })}
          {result.review.note ? ` — ${result.review.note}` : ''}
        </Alert>
      ) : (
        <p className={classes.muted}>{t('drydock.work.notReviewed')}</p>
      )}
    </div>
  );
}

interface MeasureDraft {
  label: string;
  value: number | string;
  unit: string;
  lower: number | string;
  upper: number | string;
  reference: string;
}

const MAX_MEASUREMENTS = 20;
const num = (v: number | string): number | null =>
  typeof v === 'number' ? v : v.trim() === '' ? null : Number(v);
const text = (v: string): string | null => (v.trim() === '' ? null : v.trim());

function ResultFormModal({
  code,
  work,
  current,
  source,
  onClose,
  onSaved,
}: {
  code: string;
  work: PdiInspectionWork;
  current: PdiResultRow | null;
  source: PdiInspectionSource;
  onClose: () => void;
  onSaved: (work: PdiInspectionWork) => void;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const f = (key: string) => t(`drydock.resultFields.${key}`);
  const [access, setAccess] = useState<PdiAccess>(current?.access ?? 'accessible');
  const [result, setResult] = useState<PdiResult>(current?.result ?? 'satisfactory');
  const [notes, setNotes] = useState(current?.notes ?? '');
  const [inspectedAt, setInspectedAt] = useState<string | null>(
    current?.inspectedAt ?? new Date().toISOString(),
  );
  const [inspectorId, setInspectorId] = useState<string | null>(
    current?.inspectorId ?? work.viewerId,
  );
  const [equipmentId, setEquipmentId] = useState<string | null>(current?.equipmentId ?? null);
  const [measures, setMeasures] = useState<MeasureDraft[]>(
    (current?.measurements ?? []).map((m) => ({
      label: m.label,
      value: m.value,
      unit: m.unit ?? '',
      lower: m.lower ?? '',
      upper: m.upper ?? '',
      reference: m.reference ?? '',
    })),
  );
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Erişilemeyen nokta yalnız Deferred alır (After Docking kuyruğu)
  const results = access === 'accessible' ? PDI_RESULTS : (['deferred'] as const);
  const needsNote =
    result === 'attention_required' || result === 'unsatisfactory'
      ? 'notesRequired'
      : result === 'not_applicable' || result === 'deferred'
        ? 'reasonRequired'
        : null;

  const update = (index: number, patch: Partial<MeasureDraft>) =>
    setMeasures((all) => all.map((m, i) => (i === index ? { ...m, ...patch } : m)));

  async function save() {
    if (!source.saveResult || !inspectedAt || !inspectorId) return;
    setBusy(true);
    setError(null);
    const input: PdiResultInput = {
      checkpointCode: code,
      equipmentId,
      access,
      result,
      notes: text(notes),
      measurements: measures.map((m) => ({
        label: m.label.trim(),
        value: num(m.value) ?? Number.NaN,
        unit: text(m.unit),
        lower: num(m.lower),
        upper: num(m.upper),
        reference: text(m.reference),
      })),
      inspectedAt,
      inspectorId,
      correctionReason: current ? text(reason) : null,
    };
    try {
      onSaved(
        await source.saveResult({
          inspectionId: work.id,
          supersedesId: current?.id ?? null,
          result: input,
        }),
      );
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      opened
      onClose={onClose}
      size="xl"
      title={t(current ? 'drydock.work.correctTitle' : 'drydock.work.recordTitle', { code })}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        {current ? <Alert color="gray">{t('drydock.work.correctHint')}</Alert> : null}
        {error ? <Alert color="red">{error}</Alert> : null}
        <div className={classes.formGrid}>
          <Select
            label={f('access')}
            data={PDI_ACCESS.map((value) => ({ value, label: t(`drydock.work.access.${value}`) }))}
            value={access}
            allowDeselect={false}
            onChange={(value) => {
              const next = (value as PdiAccess | null) ?? 'accessible';
              setAccess(next);
              if (next !== 'accessible') setResult('deferred');
            }}
          />
          <Select
            label={f('result')}
            required
            data={results.map((value) => ({ value, label: t(`drydock.work.status.${value}`) }))}
            value={result}
            allowDeselect={false}
            onChange={(value) => setResult((value as PdiResult | null) ?? 'satisfactory')}
          />
          <DateTimeLtUtcInput
            label={f('inspectedAt')}
            value={inspectedAt}
            onChange={setInspectedAt}
            tzOffsetMin={work.tzOffsetMin}
            required
          />
          <Select
            label={f('inspector')}
            required
            data={work.people.map((p) => ({ value: p.userId, label: p.name }))}
            value={inspectorId}
            searchable
            allowDeselect={false}
            onChange={setInspectorId}
          />
          <Select
            label={f('equipment')}
            placeholder={f('noEquipment')}
            data={work.equipment.map((e) => ({
              value: e.id,
              label: e.code ? `${e.code} · ${e.name}` : e.name,
            }))}
            value={equipmentId}
            searchable
            clearable
            onChange={setEquipmentId}
          />
        </div>
        <Textarea
          label={f('notes')}
          description={needsNote ? f(needsNote) : undefined}
          required={needsNote !== null}
          autosize
          minRows={3}
          maxLength={4000}
          value={notes}
          onChange={(event) => setNotes(event.currentTarget.value)}
        />
        <Panel title={f('measurements')}>
          <div className={classes.detail}>
            {measures.length === 0 ? <p className={classes.muted}>{f('noMeasurements')}</p> : null}
            {measures.map((m, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: taslak satırların kimliği yok
              <div key={index} className={classes.measureRow}>
                <TextInput
                  label={f('label')}
                  required
                  maxLength={120}
                  value={m.label}
                  onChange={(event) => update(index, { label: event.currentTarget.value })}
                />
                <NumberInput
                  label={f('value')}
                  required
                  decimalScale={4}
                  value={m.value}
                  onChange={(value) => update(index, { value })}
                />
                <TextInput
                  label={f('unit')}
                  maxLength={40}
                  value={m.unit}
                  onChange={(event) => update(index, { unit: event.currentTarget.value })}
                />
                <NumberInput
                  label={f('lower')}
                  decimalScale={4}
                  value={m.lower}
                  onChange={(value) => update(index, { lower: value })}
                />
                <NumberInput
                  label={f('upper')}
                  decimalScale={4}
                  value={m.upper}
                  onChange={(value) => update(index, { upper: value })}
                />
                <TextInput
                  label={f('reference')}
                  maxLength={200}
                  value={m.reference}
                  onChange={(event) => update(index, { reference: event.currentTarget.value })}
                />
                <Button
                  variant="subtle"
                  color="red"
                  aria-label={f('removeMeasurement')}
                  onClick={() => setMeasures((all) => all.filter((_, i) => i !== index))}
                >
                  <IconTrash size={16} aria-hidden />
                </Button>
              </div>
            ))}
            {measures.length < MAX_MEASUREMENTS ? (
              <Group>
                <Button
                  variant="light"
                  size="xs"
                  leftSection={<IconPlus size={14} aria-hidden />}
                  onClick={() =>
                    setMeasures((all) => [
                      ...all,
                      { label: '', value: '', unit: '', lower: '', upper: '', reference: '' },
                    ])
                  }
                >
                  {f('addMeasurement')}
                </Button>
              </Group>
            ) : null}
          </div>
        </Panel>
        {current ? (
          <Textarea
            label={f('correctionReason')}
            required
            autosize
            minRows={2}
            maxLength={1000}
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
        ) : null}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button
            loading={busy}
            disabled={!inspectedAt || !inspectorId}
            onClick={() => void save()}
          >
            {t('drydock.save')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

function ApplicabilityModal({
  code,
  profile,
  source,
  onClose,
  onSaved,
}: {
  code: string;
  profile: PdiApplicabilityRow | null;
  source: PdiInspectionSource;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [value, setValue] = useState<PdiApplicability>(profile?.applicability ?? 'review');
  const [reason, setReason] = useState(profile?.reason ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!source.setApplicability) return;
    setBusy(true);
    setError(null);
    try {
      await source.setApplicability({
        checkpointCode: code,
        ...(profile ? { rowVersion: profile.rowVersion } : {}),
        applicability: { applicability: value, reason: text(reason) },
      });
      await onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      opened
      onClose={onClose}
      title={t('drydock.work.applicabilityTitle', { code })}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p className={classes.muted}>{t('drydock.work.applicabilityHint')}</p>
        {error ? <Alert color="red">{error}</Alert> : null}
        <Select
          label={t('drydock.resultFields.applicability')}
          data={PDI_APPLICABILITY.map((v) => ({
            value: v,
            label: t(`drydock.work.applicability.${v}`),
          }))}
          value={value}
          allowDeselect={false}
          onChange={(v) => setValue((v as PdiApplicability | null) ?? 'review')}
        />
        <Textarea
          label={t('drydock.resultFields.applicabilityReason')}
          required={value === 'not_applicable'}
          autosize
          minRows={2}
          maxLength={1000}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button loading={busy} onClick={() => void save()}>
            {t('drydock.save')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

function ReviewModal({
  result,
  source,
  onClose,
  onSaved,
}: {
  result: PdiResultRow;
  source: PdiInspectionSource;
  onClose: () => void;
  onSaved: (work: PdiInspectionWork) => void;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!source.review) return;
    setBusy(true);
    setError(null);
    try {
      onSaved(await source.review({ resultId: result.id, note: text(note) }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      opened
      onClose={onClose}
      title={t('drydock.work.reviewTitle', { code: result.checkpointCode })}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p className={classes.muted}>{t('drydock.work.reviewHint')}</p>
        {error ? <Alert color="red">{error}</Alert> : null}
        <Textarea
          label={t('drydock.resultFields.reviewNote')}
          autosize
          minRows={2}
          maxLength={1000}
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button loading={busy} onClick={() => void save()}>
            {t('drydock.work.actions.review')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

/** Denetim kapanışı (R15; Master, Chief Engineer): her bulgu incelenmiş olmalı. */
function CloseInspectionModal({
  work,
  source,
  onClose,
  onDone,
}: {
  work: PdiInspectionWork;
  source: PdiInspectionSource;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function confirm() {
    if (!source.closeInspection) return;
    setBusy(true);
    setError(null);
    try {
      await source.closeInspection({ inspectionId: work.id, rowVersion: work.rowVersion });
      await onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      opened
      onClose={onClose}
      title={t('drydock.yard.closeInspectionTitle', { no: work.inspectionNo })}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <p className={classes.muted}>{t('drydock.yard.closeInspectionHint')}</p>
      {error ? <Alert color="red">{error}</Alert> : null}
      <Group justify="flex-end" gap="xs" mt="md">
        <Button variant="default" onClick={onClose}>
          {t('drydock.cancel')}
        </Button>
        <Button loading={busy} onClick={() => void confirm()}>
          {t('drydock.yard.actions.closeInspection')}
        </Button>
      </Group>
    </Modal>
  );
}
